import { ExtractComponentProp } from '@approvalmax/types';
import { SearchAccountImage } from '@approvalmax/ui';
import { Box, Button, Form, Grid, Popup, Radio, Select, Text } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch } from 'modules/react-redux';
import { selectXeroDeliveryAddress } from 'pages/requestForm/actions/xero';
import { XeroContact } from 'pages/requestForm/data/xero/XeroContact';
import { getXeroAddressTypeText } from 'pages/requestForm/selectors/xero';
import { memo, useCallback, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useForm } from 'react-hook-form';

import { qaPrefix } from '../../XeroContactAddressSearchPopup.constants';
import { useAllContacts } from './Content.hooks';
import { messages } from './Content.messages';
import { schema } from './Content.schema';
import { FormFieldId, FormValues } from './Content.types';

const qa = bemFactory.qa(qaPrefix);

export const Content = memo(() => {
    const dispatch = useDispatch();
    const { contacts, isFetching, trigger, setQuery } = useAllContacts();
    const [contact, setContact] = useState<XeroContact | null>(null);

    const form = useForm<FormValues>({
        defaultValues: {
            contactId: null,
            addressId: null,
        },
        resolver: zodResolver(schema),
    });

    const addressId = form.watch(FormFieldId.addressId);

    const handleContactChange = useCallback<ExtractComponentProp<typeof Select, 'onChange'>>(
        (contactId: string) => {
            const contact = contacts.find((contact) => contact.id === contactId) ?? null;
            const addressId = (contact && contact.addresses && contact.addresses[0])?.id ?? null;

            setContact(contact); // save in cache to be independent from contacts
            form.setValue(FormFieldId.addressId, addressId);
        },
        [contacts, form]
    );

    const handleSubmit = form.handleSubmit(({ addressId }) => {
        const address = contact?.addresses?.find((address) => address.id === addressId);

        if (!address) {
            return;
        }

        dispatch(selectXeroDeliveryAddress(address, contact));
    });

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button
                        color='blue80'
                        size='medium'
                        type='submit'
                        disabled={!form.formState.isValid}
                        data-qa={qa('done-button')}
                    >
                        {messages.button}
                    </Button>
                }
            />

            <Popup.Body>
                <Grid gap={16}>
                    <Select
                        name={FormFieldId.contactId}
                        size='small'
                        items={contacts}
                        progress={isFetching}
                        onFocus={trigger}
                        onInputChange={setQuery}
                        placeholder={messages.contactDropdownPlaceholder}
                        preventSearch
                        itemNameKey='text'
                        autocomplete
                        onChange={handleContactChange}
                        clearable={false}
                        qa={qa('search-dropdown')}
                    />

                    {contact ? (
                        <Radio.Group
                            name={FormFieldId.addressId}
                            block
                            size='small'
                            spacing='0'
                            qa={qa('address-list')}
                        >
                            {contact.addresses?.map((addressLocal) => (
                                <Box
                                    key={addressLocal.id}
                                    width='100%'
                                    color={addressLocal?.id === addressId ? 'blue10' : undefined}
                                    spacing='12 8'
                                    radius='xsmall'
                                    data-qa={qa('address-list-item')}
                                    data-qa-id={addressLocal.id}
                                    data-qa-name={addressLocal.text}
                                >
                                    <Radio value={addressLocal.id}>
                                        <Grid gap={4}>
                                            <Text
                                                font='label'
                                                fontSize='medium'
                                                fontWeight='medium'
                                                color='midnight100'
                                            >
                                                {getXeroAddressTypeText(addressLocal)}
                                            </Text>

                                            <Text font='body' fontSize='medium' color='midnight70'>
                                                {addressLocal.text}
                                            </Text>

                                            {addressLocal.attentionTo && (
                                                <Text font='body' fontSize='medium' color='midnight70'>
                                                    {addressLocal.attentionTo}
                                                </Text>
                                            )}
                                        </Grid>
                                    </Radio>
                                </Box>
                            ))}
                        </Radio.Group>
                    ) : (
                        <Grid
                            gap={24}
                            justifyItems='center'
                            alignItems='center'
                            alignContent='center'
                            minHeight={276}
                            gridTemplateRows='repeat(3, max-content)'
                        >
                            <SearchAccountImage width={100} height={100} />

                            <Text font='headline' fontSize='small' fontWeight='medium' color='midnight80'>
                                {messages.emptyListPlaceholderTitle}
                            </Text>

                            <Text font='body' fontSize='medium' color='midnight100'>
                                {messages.emptyListPlaceholder}
                            </Text>
                        </Grid>
                    )}
                </Grid>
            </Popup.Body>
        </Form>
    );
});

Content.displayName = 'Content';
